import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import OfflineIndicator from "@/components/OfflineIndicator";
import PinLogin from "@/pages/PinLogin";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Attendance from "@/pages/Attendance";
import AttendanceReports from "@/pages/AttendanceReports";
import Payments from "@/pages/Payments";
import PaymentReports from "@/pages/PaymentReports";
import FormsLibrary from "@/pages/FormsLibrary";
import SifuLedger from "@/pages/SifuLedger";
import Settings from "@/pages/Settings";
import PrivateLessons from "@/pages/PrivateLessons";
import BillingReports from "@/pages/BillingReports";
import Layout from "@/components/Layout";

// Lazy-loaded modules
const Grading = lazy(() => import("@/pages/Grading"));
const ExaminerPortal = lazy(() => import("@/pages/ExaminerPortal"));

// v7.3: Custom branded logo badge (replaces Emergent badge)
function LoongFuBadge() {
  return (
    <img
      data-testid="loongfu-badge"
      src="/assets/loongfutong.png"
      alt="Loong Fu Tong"
      style={{
        position: 'fixed',
        bottom: '16px',
        right: '16px',
        zIndex: 9999,
        width: '180px',
        height: 'auto',
        pointerEvents: 'none',
      }}
    />
  );
}

function App() {
  // Initialize auth state directly from sessionStorage to prevent redirect race condition
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => sessionStorage.getItem("loongfu_auth") === "true"
  );
  const [dashboardKey, setDashboardKey] = useState(0);

  const handleLogin = () => {
    sessionStorage.setItem("loongfu_auth", "true");
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("loongfu_auth");
    setIsAuthenticated(false);
  };

  // v7.3: Axios 401 interceptor — redirect to login on expired/invalid session
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          sessionStorage.removeItem("loongfu_auth");
          setIsAuthenticated(false);
        }
        return Promise.reject(error);
      }
    );
    // v7.3: Hide Emergent badge (inline !important overrides CSS, so use JS)
    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) { badge.style.setProperty('display', 'none', 'important'); badge.remove(); }
    };
    hideBadge();
    // Observer in case badge gets re-injected
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { axios.interceptors.response.eject(interceptor); observer.disconnect(); };
  }, []);

  // Retry handler for Dashboard - forces re-mount
  const handleDashboardRetry = useCallback(() => {
    setDashboardKey(prev => prev + 1);
  }, []);

  return (
    <div className="App min-h-screen bg-[#09090b]">
      <Toaster position="top-right" richColors />
      <OfflineIndicator />
      <LoongFuBadge />
      <BrowserRouter>
        <Routes>
          {/* Public route — Examiner Portal (no auth required) */}
          <Route path="/grade/:token" element={
            <Suspense fallback={<div className="min-h-screen bg-[#09090b] flex items-center justify-center"><div className="text-[#A1A1AA]">Loading...</div></div>}>
              <ExaminerPortal />
            </Suspense>
          } />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <PinLogin onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                <Layout onLogout={handleLogout}>
                  <Routes>
                    <Route 
                      path="/" 
                      element={
                        <ErrorBoundary 
                          fallbackMessage="Failed to load the dashboard. This might be a temporary issue with the server."
                          onRetry={handleDashboardRetry}
                        >
                          <Dashboard key={dashboardKey} />
                        </ErrorBoundary>
                      } 
                    />
                    <Route path="/students" element={<Students />} />
                    <Route path="/forms-library" element={<FormsLibrary />} />
                    <Route path="/attendance" element={<Attendance />} />
                    <Route path="/attendance-reports" element={<AttendanceReports />} />
                    <Route path="/payments" element={<Payments />} />
                    <Route path="/payment-reports" element={<PaymentReports />} />
                    <Route path="/sifu-ledger" element={<SifuLedger />} />
                    <Route path="/private-lessons" element={<PrivateLessons />} />
                    <Route path="/billing-reports" element={<BillingReports />} />
                    <Route path="/grading" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><div className="text-[#A1A1AA]">Loading...</div></div>}><Grading /></Suspense>} />
                    <Route path="/settings" element={<Settings />} />
                  </Routes>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
